import { Reveal } from '@progress/kendo-react-animation';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { NumericTextBox, RadioGroup } from '@progress/kendo-react-inputs';
import clsx from 'clsx';

import React, { ReactNode, useMemo, useState } from 'react';
import {
    AddOutline,
    ArrowUpOutline,
    CheckmarkOutline,
    ChevronDownOutline,
    ChevronUpOutline,
    CloseOutline,
    RefreshOutline,
    TrashOutline,
    UserGroupOutline,
    UserOutline,
    WarningTriangleOutline
} from '../../../../../../../icons/icons';
import { AircraftThresholdEffective, AircraftThresholdMergeResult } from '../../../../../../../monitoring/merger/aircraft-threshold-merger';
import {
    AirportThresholdEffective,
    AirportThresholdLevelInheritanceInfo,
    AirportThresholdLevelInheritanceInfoItem,
    AirportThresholdMergeResult
} from '../../../../../../../monitoring/merger/airport-threshold-merger';
import { ThresholdValidationErrors } from '../../../../../../../monitoring/merger/threshold-level-merger';
import { ThresholdLevelActionTypeOptions } from '../../../../../../../types/dropdown-options';
import {
    AircraftThresholdType,
    AirportThresholdGroupUsage,
    AirportThresholdType,
    CompanyThresholdsMode,
    MonitorAircraftThreshold,
    MonitorAirportThreshold,
    MonitorThresholdLevelModel,
    ThresholdControlType,
    ThresholdInfo,
    ThresholdLevel,
    ThresholdLevelActionType
} from '../../../../../../../types/thresholds';
import { CheckboxThreeState } from '../../../../../../components/inputs/CheckboxThreeState';
import { DropdownListWithValueField } from '../../../../../../components/inputs/DropdownListWithValueField';
import { AlertRepeatsIconIcon, AlertTriggerImprovingIcon } from './AlertIcons';
import { levelToKeyMap } from './common';

export const AircraftSettingsEditThresholdLevel = makeComponent<AircraftThresholdType>();
export const AirportSettingsEditThresholdLevel = makeComponent<AirportThresholdType>();
type MergeResultFor<T> = T extends AircraftThresholdType ? AircraftThresholdMergeResult : AirportThresholdMergeResult;
type ThresholdFor<T> = T extends AircraftThresholdType ? MonitorAircraftThreshold : MonitorAirportThreshold;
type Props<T> = {
    type: T;
    info: ThresholdInfo;
    level: ThresholdLevel;
    mode: CompanyThresholdsMode;
    threshold: MergeResultFor<T>;
    onEdit?: (type: T, level: ThresholdLevel, recipe: (draft: ThresholdFor<T>) => void) => void;
} & (
    | {
          kind: 'airport';
          thresholdHierarchy: AirportThresholdLevelInheritanceInfo;
      }
    | {
          kind: 'aircraft';
      }
);

function makeComponent<T extends AircraftThresholdType | AirportThresholdType>() {
    return React.memo((props: Props<T>) => {
        const definition = props.threshold.definition;
        const inherited = props.threshold.inherited;
        const effective = props.threshold.effective;
        const info = props.info;
        const thisLevelKey = levelToKeyMap[props.level];
        const thisLevelDefinition = definition?.[thisLevelKey];
        const thisLevelEffective = effective?.[thisLevelKey];
        const thisLevelDefined = !!thisLevelDefinition;
        const isIgnoringInherited = definition?.ignoredInheritedLevels?.includes(props.level) ?? false;
        const inheritedLevel = inherited?.[thisLevelKey];
        const isMinimumMode = props.mode === CompanyThresholdsMode.minimum;
        const actionOptions = useMemo(
            () =>
                ThresholdLevelActionTypeOptions.map((x) => ({
                    value: x.value,
                    label: x.text,
                    disabled: isMinimumMode && inheritedLevel != null && x.value < inheritedLevel.actionType
                })),
            [isMinimumMode, inheritedLevel]
        );
        const maximumActionTime = isMinimumMode ? (inheritedLevel?.actionTimeMinutes ?? 360) : 360;

        let valueMin = info.min;
        let valueMax = info.max;
        if (info.controlType === ThresholdControlType.input) {
            if (info.customUnits.length > 0) {
                const selectedUnit = info.customUnits[thisLevelDefinition?.unitIndex ?? 0];
                valueMin *= selectedUnit.multiplier;
                valueMax *= selectedUnit.multiplier;

                if (info.decimal) {
                    valueMin /= 100;
                    valueMax /= 100;
                }
            }
        }

        const patchThreshold = (produce: (draft: ThresholdFor<T>) => void) => {
            props.onEdit?.(props.type, props.level, produce);
        };

        const removeLevel = () => {
            props.onEdit?.(props.type, props.level, (x) => (x[thisLevelKey] = null));
        };

        const createLevel = () => {
            props.onEdit?.(props.type, props.level, (x) => {
                x[thisLevelKey] = createNewLevel(props.level, props.threshold.effective);
                // Clear ignored because we're overriding it anyway
                x.ignoredInheritedLevels = x.ignoredInheritedLevels.filter((x) => x !== props.level);
            });
        };

        return (
            <div style={{ width: 260 }} className="d-flex flex-column gap-2">
                {props.kind === 'airport' ? (
                    <InheritedLevels
                        info={info}
                        thresholdHierarchy={props.thresholdHierarchy}
                        onIgnoredChanged={(newValue) =>
                            props.onEdit?.(props.type, props.level, (t) => {
                                if (newValue) {
                                    t.ignoredInheritedLevels.push(props.level);
                                } else {
                                    t.ignoredInheritedLevels = t.ignoredInheritedLevels.filter((x) => x !== props.level) ?? [];
                                }
                            })
                        }
                        canChangeIgnore={!thisLevelDefined}
                        ignored={isIgnoringInherited}
                    />
                ) : (
                    <>
                        <ThresholdLevelCard
                            level={inherited?.[thisLevelKey]}
                            faded={isIgnoringInherited}
                            title={
                                <span>
                                    <SvgIcon icon={UserGroupOutline} className="icon-b-1" />
                                    <span> Company {props.mode === CompanyThresholdsMode.template ? 'template' : 'minimum'}</span>
                                </span>
                            }
                            info={info}
                            footer={
                                !thisLevelDefinition &&
                                !isMinimumMode && (
                                    <Button
                                        className="border-top rounded-0 w-100 mt-2"
                                        fillMode={'flat'}
                                        themeColor={isIgnoringInherited ? 'success' : 'error'}
                                        svgIcon={isIgnoringInherited ? CheckmarkOutline : CloseOutline}
                                        onClick={() => {
                                            props.onEdit?.(props.type, props.level, (draft) => {
                                                if (isIgnoringInherited) {
                                                    draft.ignoredInheritedLevels = draft.ignoredInheritedLevels.filter(
                                                        (x) => x !== props.level
                                                    );
                                                } else {
                                                    draft.ignoredInheritedLevels.push(props.level);
                                                }
                                            });
                                        }}>
                                        {isIgnoringInherited ? 'Restore' : 'Ignore'}
                                    </Button>
                                )
                            }
                        />
                    </>
                )}

                {thisLevelDefinition && (
                    <div className="border rounded-1">
                        <div className="p-2">
                            <div className="secondary-text">
                                <SvgIcon icon={UserOutline} className="icon-b-1" />
                                <span> User defined</span>
                            </div>
                            <div className="d-flex flex-column gap-2">
                                <div>
                                    {/* Level */}
                                    <div className="secondary-text">Level:</div>
                                    {info.controlType === ThresholdControlType.input ? (
                                        <div className="d-flex gap-2">
                                            <NumericTextBox
                                                step={1}
                                                min={valueMin}
                                                max={valueMax}
                                                value={info.decimal ? thisLevelDefinition.value / 100 : thisLevelDefinition.value}
                                                format={
                                                    (info.decimal ? '0.00' : '0') + (info.customUnits.length > 0 ? '' : ` '${info.units}'`)
                                                }
                                                onChange={(e) =>
                                                    patchThreshold((threshold) => {
                                                        const value = info.decimal ? (e.value ?? 0) * 100 : (e.value ?? 0);
                                                        threshold[thisLevelKey]!.value = value;
                                                    })
                                                }
                                            />
                                            {info.customUnits.length > 0 && (
                                                <DropdownListWithValueField
                                                    onChange={(e) =>
                                                        patchThreshold((t) => {
                                                            const level = t[thisLevelKey]!;
                                                            const currentUnit = info.customUnits[level.unitIndex ?? 0];
                                                            const newUnit = info.customUnits[e.value ?? 0];
                                                            const valueCommonUnit = (level.value ?? 0) / currentUnit.multiplier;
                                                            level.unitIndex = e.value ?? 0;
                                                            level.value = Math.round(valueCommonUnit * newUnit.multiplier);
                                                        })
                                                    }
                                                    style={{ width: 150 }}
                                                    value={thisLevelDefinition.unitIndex ?? 0}
                                                    data={info.customUnits.map((x, i) => ({ value: i, text: x.displayName }))}
                                                />
                                            )}
                                        </div>
                                    ) : info.controlType === ThresholdControlType.dropdown ? (
                                        <RadioGroup
                                            value={thisLevelDefinition.value}
                                            data={info.dropdownOptions.map((x, i) => ({ value: i, label: x }))}
                                            onChange={(e) => patchThreshold((t) => (t[thisLevelKey]!.value = e.value ?? 0))}
                                        />
                                    ) : (
                                        info.controlType === ThresholdControlType.checkbox && <SvgIcon icon={CheckmarkOutline} />
                                    )}
                                </div>

                                {/* Errors */}
                                {thisLevelEffective && thisLevelEffective.errors !== ThresholdValidationErrors.none && (
                                    <div className="text-error">
                                        <div className="d-flex flex-column gap-1">
                                            {(thisLevelEffective.errors & ThresholdValidationErrors.progression) !== 0 && (
                                                <div>
                                                    <SvgIcon className="icon-b-1" icon={WarningTriangleOutline} themeColor="error" />
                                                    <span>Values must progress in worsening direction from info to emergency.</span>
                                                </div>
                                            )}
                                            {(thisLevelEffective.errors & ThresholdValidationErrors.companyMinimumNotMet) !== 0 && (
                                                <div>
                                                    <SvgIcon icon={WarningTriangleOutline} themeColor="error" className="icon-b-1" />
                                                    <span>Company minimum is not met.</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Alerting */}
                                <div>
                                    <div className="secondary-text">Alerting:</div>
                                    {info.canRepeat && (
                                        <CheckboxThreeState
                                            defaultChecked={true}
                                            checked={thisLevelDefinition.repeat}
                                            onChange={(e) => patchThreshold((t) => (t[thisLevelKey]!.repeat = e.value))}
                                            label={
                                                <>
                                                    <SvgIcon className="icon-b-1" icon={RefreshOutline} />
                                                    <span> Repeat alert</span>
                                                </>
                                            }
                                        />
                                    )}

                                    <CheckboxThreeState
                                        checked={thisLevelDefinition.triggerOnImproving}
                                        onChange={(e) => patchThreshold((t) => (t[thisLevelKey]!.triggerOnImproving = e.value))}
                                        label={
                                            <>
                                                <SvgIcon className="icon-b-1" icon={ArrowUpOutline} />
                                                <span> Alert on improving</span>
                                            </>
                                        }
                                    />
                                </div>

                                {/* Action */}
                                <div>
                                    <div className="secondary-text">Action:</div>
                                    <RadioGroup
                                        value={thisLevelDefinition.actionType}
                                        data={actionOptions}
                                        onChange={(e) => patchThreshold((t) => (t[thisLevelKey]!.actionType = e.value))}
                                    />
                                    {thisLevelDefinition.actionType === ThresholdLevelActionType.withinMinutes && (
                                        <div>
                                            <div className="secondary-text">Action within:</div>
                                            <NumericTextBox
                                                value={thisLevelDefinition.actionTimeMinutes}
                                                format={'0 minutes'}
                                                max={maximumActionTime}
                                                min={1}
                                                onChange={(e) => patchThreshold((t) => (t[thisLevelKey]!.actionTimeMinutes = e.value))}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <Button
                            className="w-100 border-top rounded-0"
                            fillMode={'flat'}
                            svgIcon={TrashOutline}
                            themeColor="error"
                            onClick={() => removeLevel()}>
                            Remove
                        </Button>
                    </div>
                )}

                {!thisLevelDefinition && (
                    <Button fillMode={'outline'} themeColor="primary" svgIcon={AddOutline} onClick={() => createLevel()}>
                        {inheritedLevel ? 'Override' : 'Add'}
                    </Button>
                )}
            </div>
        );
    });
}

function InheritedLevels(props: {
    thresholdHierarchy: AirportThresholdLevelInheritanceInfo;
    info: ThresholdInfo;
    canChangeIgnore: boolean;
    ignored: boolean;
    onIgnoredChanged: (value: boolean) => void;
}) {
    const { info } = props;
    // .slice(1) to skip this level
    const ownLevels = (props.thresholdHierarchy?.own.slice(1) ?? []).filter((x) => x.level).toReversed();
    const inheritedLevels = (props.thresholdHierarchy?.inherited ?? []).filter((x) => x.level).toReversed();
    const [open, setOpen] = useState(ownLevels.length + inheritedLevels.length === 1);
    const hasAnyInheritedLevels = ownLevels.length + inheritedLevels.length > 0;
    if (!hasAnyInheritedLevels) {
        return null;
    }

    return (
        <div>
            <div className="d-flex gap-1 align-items-center py-1">
                <Button
                    fillMode={'flat'}
                    className="button-small"
                    svgIcon={open ? ChevronUpOutline : ChevronDownOutline}
                    onClick={() => setOpen(!open)}
                />
                <span>Inherited levels</span>
                <span className="fe-badge px-1">{ownLevels.length + inheritedLevels.length}</span>
            </div>

            <Reveal className="w-100">
                {open && (
                    <div>
                        <div className="d-flex flex-column gap-2 pb-2">
                            {inheritedLevels.map((x, i) => (
                                <AirportInheritedLevel
                                    key={i}
                                    item={x}
                                    info={info}
                                    ignored={props.ignored}
                                    prefix={
                                        <span>
                                            <SvgIcon icon={UserGroupOutline} className="icon-b-1" />
                                            <span> Company:</span>
                                        </span>
                                    }
                                />
                            ))}
                        </div>
                        <div className="d-flex flex-column gap-2">
                            {ownLevels.map((x, i) => (
                                <AirportInheritedLevel
                                    key={i}
                                    item={x}
                                    info={info}
                                    ignored={props.ignored}
                                    prefix={
                                        <span>
                                            <SvgIcon icon={UserOutline} className="icon-b-1" />
                                            <span> User:</span>
                                        </span>
                                    }
                                />
                            ))}
                        </div>
                    </div>
                )}
            </Reveal>

            {hasAnyInheritedLevels && props.canChangeIgnore && (
                <Button
                    className="border w-100 mt-2"
                    fillMode={'flat'}
                    themeColor={props.ignored ? 'success' : 'error'}
                    svgIcon={props.ignored ? CheckmarkOutline : CloseOutline}
                    onClick={() => {
                        props.onIgnoredChanged(!props.ignored);
                    }}>
                    {props.ignored ? 'Restore' : 'Ignore'}
                </Button>
            )}
        </div>
    );
}

function AirportInheritedLevel({
    item,
    info,
    ignored,
    prefix
}: {
    item: AirportThresholdLevelInheritanceInfoItem;
    info: ThresholdInfo;
    ignored: boolean;
    prefix?: React.ReactNode;
}) {
    const level = item.level;
    if (!level) {
        return null;
    }

    return (
        <ThresholdLevelCard
            level={item.level}
            info={info}
            faded={ignored}
            crossedOut={item.group?.isOff}
            title={
                <div className="d-flex justify-content-between align-items-center">
                    <div className="secondary-text">
                        {prefix && <span>{prefix} </span>}
                        {item.group ? (
                            <span>
                                {item.group.airportId || 'Default'} - {AirportThresholdGroupUsage[item.group.usage]}
                            </span>
                        ) : (
                            <span>{item.name}</span>
                        )}
                    </div>
                    {item.group?.isOff && <div className="fe-badge bg-danger">OFF</div>}
                </div>
            }
        />
    );
}

function ThresholdLevelCard({
    info,
    level,
    crossedOut,
    faded,
    footer,
    title
}: {
    level: MonitorThresholdLevelModel | null | undefined;
    info: ThresholdInfo;
    title?: ReactNode;
    footer?: ReactNode;
    faded?: boolean;
    crossedOut?: boolean;
}) {
    if (!level) {
        return null;
    }

    return (
        <div className={'border rounded-1'}>
            <div className={clsx(faded && 'opacity-50')}>
                <div className="border-bottom px-2 py-1 secondary-text">{title}</div>
                <div className="d-flex gap-1 px-2 pt-1">
                    <span>Value:</span>
                    <span className={clsx('fw-bold', crossedOut && 'text-decoration-line-through')}>
                        {info.controlType === ThresholdControlType.input ? (
                            <>
                                <span>{level.value} </span>
                                {info.customUnits.length > 0 ? (
                                    <span> {info.customUnits[level.unitIndex ?? 0].displayName}</span>
                                ) : (
                                    <span> {info.units}</span>
                                )}
                            </>
                        ) : info.controlType === ThresholdControlType.dropdown ? (
                            <span>{info.dropdownOptions[level.value]}</span>
                        ) : (
                            <SvgIcon icon={CheckmarkOutline} />
                        )}
                    </span>
                    <AlertRepeatsIconIcon show={level.repeat} />
                    <AlertTriggerImprovingIcon show={level.triggerOnImproving} />
                </div>
                <div className="px-2">
                    <span>Action: </span>
                    <span className={clsx('fw-bold', crossedOut && 'text-decoration-line-through')}>
                        {level.actionType === ThresholdLevelActionType.withinMinutes ? (
                            <>
                                <span>Within</span>
                                <span> {level.actionTimeMinutes} </span>
                                <span>minutes</span>
                                <span>{(level.actionTimeMinutes ?? 0) > 1 ? 's' : ''}</span>
                            </>
                        ) : (
                            ThresholdLevelActionTypeOptions.find((x) => x.value === level.actionType)?.text
                        )}
                    </span>
                </div>
            </div>
            {footer}
        </div>
    );
}

function createNewLevel(
    level: ThresholdLevel,
    threshold: AircraftThresholdEffective | AirportThresholdEffective | null
): MonitorThresholdLevelModel {
    /*let lowerBound: ThresholdLevelMergeResult | null = null;
    let upperBound: ThresholdLevelMergeResult | null = null;

    // TODO: unfinished
    if (threshold) {
        let l = level;
        while (--l >= ThresholdLevel.info) {
            if (threshold[levelToKeyMap[l]]) {
                lowerBound = threshold[levelToKeyMap[l]];
                break;
            }
        }

        l = level;
        while (++l < ThresholdLevel.emergency) {
            if (threshold[levelToKeyMap[l]]) {
                upperBound = threshold[levelToKeyMap[l]];
                break;
            }
        }
    }*/

    const newLevel: MonitorThresholdLevelModel = {
        repeat: null,
        triggerOnImproving: null,
        value: 0,
        unitIndex: null,
        actionType: ThresholdLevelActionType.none,
        actionTimeMinutes: null
    };

    // Set default action type and types based on threshold level
    switch (level) {
        case ThresholdLevel.watch:
            newLevel.actionType = ThresholdLevelActionType.possible;
            break;
        case ThresholdLevel.warning:
            newLevel.actionType = ThresholdLevelActionType.withinMinutes;
            newLevel.actionTimeMinutes = 30;
            break;
        case ThresholdLevel.critical:
            newLevel.actionType = ThresholdLevelActionType.withinMinutes;
            newLevel.actionTimeMinutes = 120;
            break;
        case ThresholdLevel.emergency:
            newLevel.actionType = ThresholdLevelActionType.immediate;
            break;
    }

    /* TODO: unfinished
    // Find out if there's already a level defined
    const currentLevel = threshold?.[levelToKeyMap[level]];
    if (currentLevel?.value) {
        // If there's we're overriding it, so copy values from that level
        newLevel.value = currentLevel.value.value;
        newLevel.unitIndex = currentLevel.value.unitIndex;
        newLevel.triggerOnImproving = currentLevel.value.triggerOnImproving;
        newLevel.repeat = currentLevel.value.repeat;
        newLevel.actionType = currentLevel.value.actionType;
        newLevel.actionTimeMinutes = currentLevel.value.actionTimeMinutes;
    } else if (lowerBound && lowerBound.value) {
        // Otherwise check the lower bound to find a value that would progress in the right direction
        // TODO: units! control type! max/min threshold value
        newLevel.value = lowerBound.value.value + 1;
    }*/
    return newLevel;
}
